import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProcessArtifactDto } from '@api/processes';
import type {
  ManualOutputFile,
  ManualWaiting,
  UseProcessRunSessionReturn,
} from './runSession/types';

type ManualSession = Pick<
  UseProcessRunSessionReturn,
  'manualWaiting' | 'liveStateForNode' | 'completeManual'
>;

export interface ManualCompletionPrompt {
  readonly manualWait: ManualWaiting | null;
  readonly inputArtifacts: readonly ProcessArtifactDto[];
  readonly isCompleting: boolean;
  readonly onComplete: (outputs: readonly ManualOutputFile[]) => Promise<void>;
  readonly onDismiss: () => void;
}

/**
 * Drives the manual-node completion prompt. Mirrors the runner-claim "Not now"
 * flow: a dismissed prompt (keyed by node+iteration) can be recovered without a
 * refresh, and the mirrored upstream inputs are read off the node's live state
 * so the assignee can download them.
 */
export function useManualCompletionPrompt(session: ManualSession): ManualCompletionPrompt {
  const [isCompleting, setIsCompleting] = useState(false);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  const activeWait = session.manualWaiting;
  const activeKey = activeWait ? `${activeWait.nodeId}:${activeWait.iteration}` : null;

  useEffect(() => {
    setDismissedKey(null);
  }, [activeKey]);

  const inputArtifacts = useMemo(
    () => (activeWait ? session.liveStateForNode(activeWait.nodeId)?.artifacts ?? [] : []),
    [activeWait, session],
  );

  const onDismiss = useCallback(() => {
    if (activeKey) setDismissedKey(activeKey);
  }, [activeKey]);

  const onComplete = useCallback(
    async (outputs: readonly ManualOutputFile[]) => {
      if (isCompleting) return;
      setIsCompleting(true);
      try {
        await session.completeManual(outputs);
      } finally {
        setIsCompleting(false);
      }
    },
    [isCompleting, session],
  );

  return {
    manualWait: activeWait != null && activeKey !== dismissedKey ? activeWait : null,
    inputArtifacts,
    isCompleting,
    onComplete,
    onDismiss,
  };
}
